import { roleAtLeast, ROLES, type Role } from "@/shared/utils/roles";

/**
 * O que cada cargo pode fazer, na língua da tela de criar usuário
 * (2026-10-27, pedido da dona: a matriz como pop-up ao lado do campo
 * Cargo, pra quem convida saber o que está entregando).
 *
 * **É um RESUMO das travas do servidor, não a trava.** Quem decide é o
 * papel mínimo de cada rota (`EXIGE_*` em
 * `web_api/infrastructure/http/security.py`, travado por
 * `web_api/tests/test_route_roles.py`); se os dois divergirem, o servidor
 * vence. Mexeu no papel mínimo de uma rota que aparece aqui, ajuste a
 * linha no mesmo commit. Revisada em 2026-11-01 contra as travas rota a rota.
 *
 * Os cargos são CUMULATIVOS (cada um herda tudo dos de baixo), então cada
 * linha diz só a partir de qual cargo vale; `notes` troca o "pode" de um
 * cargo por uma condição ("com chave", "no núcleo").
 */
export type Capability = {
  label: string;
  from: Role;
  notes?: Partial<Record<Role, string>>;
};

export type CapabilitySection = { title: string; rows: Capability[] };

export const ROLE_CAPABILITIES: CapabilitySection[] = [
  {
    title: "Quais empresas enxerga",
    rows: [
      {
        label: "Empresas que acessa",
        from: "viewer",
        notes: {
          viewer: "liberadas",
          technician: "liberadas",
          specialist: "liberadas",
          administrator: "do núcleo",
          master: "todas"
        }
      }
    ]
  },
  {
    title: "Ver",
    rows: [
      { label: "Menções, Justificativas e detalhe", from: "viewer" },
      { label: "Dashboard e amostras salvas", from: "viewer" },
      { label: "Configuração da empresa (só leitura)", from: "viewer" }
    ]
  },
  {
    title: "Operar o dia a dia",
    rows: [
      { label: "Corrigir classificação, revisar, descartar", from: "technician" },
      { label: "Edição em lote e vincular a outra marca", from: "technician" },
      { label: "Subir planilha e tratar fila de correção", from: "technician" },
      { label: "Gerar e salvar amostra (também pelo Versa)", from: "technician" },
      { label: "Exportar, Downloads, filtros e planilhas salvas, configuração da empresa", from: "technician" },
      { label: "Copiloto", from: "technician" },
      { label: "Versa: relatórios, modelos, envio por e-mail e WhatsApp", from: "technician" },
      { label: "Resumo da menção pela IA e notas dos textos do dashboard", from: "technician" }
    ]
  },
  {
    title: "Configurar a empresa",
    rows: [
      { label: "Cadastros de Ajustes e importação por planilha", from: "specialist" },
      { label: "Premissas, termos e foco", from: "specialist" },
      {
        label: "Textos de prompt e modelo por tarefa",
        from: "specialist",
        notes: { specialist: "com chave", administrator: "com chave" }
      },
      { label: "CENA da empresa", from: "specialist" },
      // Salvar desceu pra Técnico em 2026-11-03; "Atualizar dados" reescreve
      // uma amostra já aprovada e fica com o Especialista.
      { label: "Atualizar dados de amostra salva", from: "specialist" },
      { label: "Assistente de configuração e consultoria IA", from: "specialist" }
    ]
  },
  {
    title: "Coordenar",
    rows: [
      { label: "Criar empresa", from: "administrator", notes: { administrator: "no núcleo" } },
      { label: "Reprocessar com IA", from: "administrator" },
      // A rota de voltar menção a uma versão é de Técnico (EXIGE_TECNICO_NA_MENCAO),
      // mas a única entrada é a tela de Histórico, de Administrador: a matriz
      // descreve o que a pessoa alcança.
      {
        label: "Histórico de edições, desfazer lote e voltar menção a uma versão",
        from: "administrator"
      },
      { label: "Convidar e gerir usuários", from: "administrator", notes: { administrator: "cargos abaixo" } }
    ]
  },
  {
    title: "Administrar a instalação",
    rows: [
      { label: "Núcleos e vincular pessoa a núcleo", from: "master" },
      { label: "CENA padrão, modelos de IA, consumo, Copiloto e IA do Versa", from: "master" },
      { label: "Templates de upload e prompt padrão", from: "master" },
      { label: "Auditoria e tentativas de login", from: "master" },
      { label: "Liberar chaves de prompt e modelo", from: "master" },
      { label: "Desativar empresa e desbloquear login", from: "master" },
      { label: "Conta por senha: criar, redefinir senha e trocar a forma de entrar", from: "master" }
    ]
  }
];

/** Do menor alcance pro maior: é a ordem da leitura "cada cargo herda
 *  tudo dos cargos à esquerda". */
export const CAPABILITY_COLUMNS: Role[] = [...ROLES].reverse();

export type CapabilityCell = { kind: "yes" } | { kind: "no" } | { kind: "note"; text: string };

export function capabilityCell(row: Capability, role: Role): CapabilityCell {
  if (!roleAtLeast(role, row.from)) return { kind: "no" };
  const note = row.notes?.[role];
  return note ? { kind: "note", text: note } : { kind: "yes" };
}
